import { Box, CircularProgress, Fade } from "@mui/material"
import { ReactElement, useEffect, useMemo, useState } from "react"
import { Link as RouterLink } from "react-router-dom"

import ApplicationGenerator from "./applicationGenerator/applicationGenerator"
import SelectLct from "./selectLct/selectLct"
import { INSTALLER_DRAFTS } from "../../../router/routes"
import { useDraftLoader } from "../../../services/middleware/hooks/draftApplication/useDraftLoader"
import useHasDrafts from "../../../services/middleware/hooks/installerInterface/useHasDrafts"
import AppAlert from "../../dataDisplay/alerts/appAlert"
import ErrorDisplay from "../../fallback/errorDisplay"
import DisplayIf from "../../wrappers/displayIf"

/**
 * NewSingleApplication manages the application creation workflow.
 * It orchestrates the flow between LCT selection and application form generation,
 * handling both new applications and draft modes. In draft mode, it bypasses the LCT
 * selection step and directly loads the application with pre-populated data. In normal mode,
 * it first presents the LCT selection interface before proceeding to the application form.
 *
 * @component
 * @returns {ReactElement} A component that handles the complete flow of creating a new single application
 */
const NewSingleApplication = (): ReactElement => {
    const { isDraftMode, draftData, isLoading, error } = useDraftLoader()
    const { hasDrafts } = useHasDrafts()
    const [showAlert, setShowAlert] = useState(true)

    const shouldShowDraftsBanner = hasDrafts && !isDraftMode

    // Use useMemo to derive the initial lcts value
    const initialLcts = useMemo(
        () => (isDraftMode && draftData?.lcts ? [...draftData.lcts] : []),
        [isDraftMode, draftData]
    )

    const [displaySelectLct, setDisplaySelectLct] = useState(!isDraftMode)
    const [lcts, setLcts] = useState<string[]>(initialLcts)

    // Update lcts when draft data changes or draft mode changes
    useEffect(() => {
        if (isDraftMode && draftData?.lcts) {
            // Ensure we're always setting an array
            setLcts([...draftData.lcts])
        }
    }, [isDraftMode, draftData])

    // Handle loading states for drafts
    if (isDraftMode && isLoading) {
        return <CircularProgress />
    }

    // Handle error states for drafts
    if (isDraftMode && error) {
        return <ErrorDisplay error={error} />
    }

    return (
        <Box sx={{ position: "relative" }}>
            <Fade in={shouldShowDraftsBanner} mountOnEnter unmountOnExit>
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 3,
                    }}
                >
                    {showAlert && (
                        <AppAlert
                            severity="info"
                            sx={{ borderRadius: 2, boxShadow: 3 }}
                            onClose={() => setShowAlert(false)}
                        >
                            You have unfinished applications. You can pick up
                            where you left off from the{" "}
                            <RouterLink to={INSTALLER_DRAFTS}>
                                My Drafts
                            </RouterLink>{" "}
                            tab.
                        </AppAlert>
                    )}
                </Box>
            </Fade>
            <Box
                sx={{
                    pt: shouldShowDraftsBanner && showAlert ? 10 : 0,
                    transition: "padding-top 0.3s ease",
                }}
            >
                <DisplayIf condition={displaySelectLct && !isDraftMode}>
                    <SelectLct
                        setDisplaySelectLct={setDisplaySelectLct}
                        lcts={lcts}
                        setLcts={setLcts}
                    />
                </DisplayIf>
                <DisplayIf condition={!displaySelectLct || isDraftMode}>
                    <ApplicationGenerator
                        lcts={lcts}
                        setDisplaySelectLct={setDisplaySelectLct}
                        draftData={draftData}
                        isDraftMode={isDraftMode}
                    />
                </DisplayIf>
            </Box>
        </Box>
    )
}

export default NewSingleApplication
